/**
 * moagem-sync.js
 * Portela Cafés – Moagem Cart Note Synchronisation
 *
 * Automatically maintains an autogenerated section at the end of the cart note
 * that summarises each cart item's chosen Moagem grind type.
 *
 * Customer-written notes are preserved. Only the content between
 * [AUTO_MOAGEM_START] … [AUTO_MOAGEM_END] markers is ever replaced.
 *
 * Uses Shopify AJAX Cart API (/cart.js + /cart/update.js).
 */

(function () {
  'use strict';

  const MARKER_START = '[AUTO_MOAGEM_START]';
  const MARKER_END   = '[AUTO_MOAGEM_END]';
  const DEBOUNCE_MS  = 600;
  let syncQueue = Promise.resolve();

  // ── Helpers ──────────────────────────────────────────────────────────────

  function debounce(fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /**
   * Split the current note into { customerNote, autoNote } parts.
   * customerNote  = everything written by the customer (before the markers).
   * autoNote      = the previously-generated moagem summary (inside markers).
   */
  function splitNote(rawNote) {
    if (!rawNote) return { customerNote: '', autoNote: '' };

    const startIdx = rawNote.indexOf(MARKER_START);
    const endIdx   = rawNote.indexOf(MARKER_END);

    if (startIdx === -1 || endIdx === -1 || endIdx < startIdx) {
      // No markers → entire note is customer-written
      return { customerNote: rawNote.trimEnd(), autoNote: '' };
    }

    const customerNote = rawNote.slice(0, startIdx).trimEnd();
    const autoNote     = rawNote.slice(startIdx + MARKER_START.length, endIdx).trim();
    return { customerNote, autoNote };
  }

  /**
   * Build the autogenerated moagem note string from the cart items array.
   * Each item with a Moagem line-item property contributes one line:
   *   "Product Title — Moagem Value"
   */
  function buildAutoNote(cartItems) {
    const lines = [];

    cartItems.forEach(item => {
      const props = item.properties || {};
      const moagem = props['Moagem'] || props['moagem'] || null;
      if (moagem && moagem.toString().trim() !== '') {
        const quantity = Number(item.quantity) > 1 ? ` x ${item.quantity}` : '';
        lines.push(`${item.product_title}${quantity} — ${moagem.toString().trim()}`);
      }
    });

    return lines.join('\n');
  }

  /**
   * Combine customer note + new auto note into the final note string
   * that will be saved to the cart.
   */
  function combineNote(customerNote, autoNote) {
    if (!autoNote) {
      // Nothing to auto-generate – keep only the customer note (no markers)
      return customerNote;
    }

    const separator = customerNote ? '\n\n' : '';
    return `${customerNote}${separator}${MARKER_START}\n${autoNote}\n${MARKER_END}`;
  }

  // ── Core Sync Logic ───────────────────────────────────────────────────────

  function updateVisibleNoteTextareas(rawNote) {
    const { customerNote } = splitNote(rawNote || '');

    document
      .querySelectorAll('textarea[name="note"], #CartDrawer-Note, #cart-note')
      .forEach(textarea => {
        if (document.activeElement !== textarea) {
          textarea.value = customerNote;
        }
      });
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
  }

  function getActiveCustomerNote() {
    const activeTextarea = document.activeElement?.matches?.('textarea[name="note"], #CartDrawer-Note, #cart-note')
      ? document.activeElement
      : null;

    return activeTextarea && activeTextarea.dataset.moagemInitialised === 'true'
      ? activeTextarea.value.trimEnd()
      : null;
  }

  function saveMoagemNoteFromCart(cart, noteCart, customerNoteOverride) {
    const rawNote = (noteCart || cart).note || '';
    const { customerNote: existingCustomerNote } = splitNote(rawNote);
    const customerNote = typeof customerNoteOverride === 'string' ? customerNoteOverride : existingCustomerNote;
    const newAutoNote = buildAutoNote(cart.items || []);
    const combined = combineNote(customerNote, newAutoNote);

    updateVisibleNoteTextareas(combined);

    // Only write back if something actually changed
    if (combined === rawNote) return Promise.resolve();

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: combined }),
    });
  }

  function enqueueSync(task) {
    syncQueue = syncQueue
      .catch(() => {})
      .then(task);

    return syncQueue.catch(err => {
      console.warn('[moagem-sync] Error:', err);
    });
  }

  function syncMoagemNote(cartData, options = {}) {
    const hasCartItems = cartData && Array.isArray(cartData.items);
    const hasCartNote = hasCartItems && typeof cartData.note === 'string';
    const customerNoteOverride =
      typeof options.customerNote === 'string' ? options.customerNote : getActiveCustomerNote();

    return enqueueSync(() => {
      const cartPromise = hasCartItems ? Promise.resolve(cartData) : fetchCart();
      const noteCartPromise = hasCartNote ? cartPromise : fetchCart();

      return Promise.all([cartPromise, noteCartPromise])
        .then(([cart, noteCart]) => saveMoagemNoteFromCart(cart, noteCart, customerNoteOverride));
    });
  }

  const debouncedSync = debounce(syncMoagemNote, DEBOUNCE_MS);

  function syncMoagemNoteAfterCartSettles(cartData) {
    syncMoagemNote(cartData);
    setTimeout(syncMoagemNote, 1200);
  }

  function saveCustomerNote(customerNote) {
    return syncMoagemNote(null, { customerNote: (customerNote || '').trimEnd() });
  }

  window.MoagemSync = {
    sync: syncMoagemNote,
    saveCustomerNote,
    flush() {
      const customerNote = getActiveCustomerNote();
      return syncQueue
        .catch(() => {})
        .then(() =>
          typeof customerNote === 'string'
            ? saveCustomerNote(customerNote)
            : syncMoagemNote()
        );
    },
  };

  // ── Note Textarea Filtering ───────────────────────────────────────────────
  /**
   * When a cart note textarea is present (cart page or drawer), only show
   * the customer-written portion. On input, write back combined note.
   */
  function initNoteTextareas() {
    const textareas = document.querySelectorAll(
      'textarea[name="note"], #CartDrawer-Note, #cart-note'
    );

    textareas.forEach(textarea => {
      if (textarea.dataset.moagemInitialised) return;
      textarea.dataset.moagemInitialised = 'true';

      // Strip auto-section from visible value
      const rawValue = textarea.value || '';
      const { customerNote } = splitNote(rawValue);
      textarea.value = customerNote;

      // Keep Dawn's default cart-note handler from saving the visible-only note.
      textarea.addEventListener('input', event => {
        event.stopPropagation();
      });

      // On change, rebuild and save combined note
      textarea.addEventListener(
        'input',
        debounce(function () {
          saveCustomerNote(textarea.value || '')
            .catch(err => console.warn('[moagem-sync] Note save error:', err));
        }, DEBOUNCE_MS)
      );
    });
  }

  // ── Share Button Handler ──────────────────────────────────────────────────
  function initShareButtons() {
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.product-share-btn');
      if (!btn) return;

      e.preventDefault();
      const url = btn.getAttribute('data-share-url') || window.location.href;

      if (navigator.share) {
        navigator.share({ url })
          .catch(err => { if (err.name !== 'AbortError') console.warn(err); });
      } else {
        navigator.clipboard.writeText(url).then(() => {
          const icon = btn.querySelector('svg');
          const originalHtml = btn.innerHTML;

          btn.innerHTML = `
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#665C57"
                 stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          `;
          setTimeout(() => { btn.innerHTML = originalHtml; }, 2000);
        }).catch(() => {
          // Fallback: open a copy prompt
          prompt('Copie o link:', url);
        });
      }
    });
  }

  // ── PubSub / Event Integration ────────────────────────────────────────────
  /**
   * Hook into Dawn's PUB_SUB_EVENTS.cartUpdate so that any add-to-cart,
   * quantity change, or removal triggers a note re-sync automatically.
   */
  function attachPubSubListener() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.cartData && Array.isArray(event.cartData.items)) {
          syncMoagemNoteAfterCartSettles(event.cartData);
        } else {
          debouncedSync();
          setTimeout(syncMoagemNote, 1200);
        }
        // Re-init textareas in case the cart drawer re-rendered its HTML
        setTimeout(initNoteTextareas, 400);
      });
    } else {
      // Fallback: poll cart changes via MutationObserver on the cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const observer = new MutationObserver(debounce(() => {
          debouncedSync();
          initNoteTextareas();
        }, 500));
        observer.observe(cartDrawer, { childList: true, subtree: true });
      }
    }
  }

  // ── Boot ─────────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    // Initial sync on page load
    syncMoagemNote();

    // Initialise note textarea filtering
    initNoteTextareas();

    // Attach to Dawn cart events
    attachPubSubListener();

    // Initialise share buttons
    initShareButtons();

    // Observe DOM for dynamically added textareas (cart drawer opens)
    const observer = new MutationObserver(debounce(() => {
      initNoteTextareas();
    }, 300));
    observer.observe(document.body, { childList: true, subtree: true });
  });

})();
